// ============================================================================
// SIGNAL SCORER — single source of truth (V2)
//
// Used by:
//   - src/utils/marketEngine.js       (client: signal generator + SignalHealth)
//   - server/services/signalHealth.js (server: background health monitor)
//
// V2 changes vs V1:
//   - Indicator contributions are grouped into 6 clusters
//     (momentum, trend, volatility, volume, structure, macro).
//   - Each cluster's contribution to total score is capped at +2, so a single
//     "oversold momentum" setup can't be triple-counted (RSI + Stoch + Weekly-RSI).
//   - The top-contributing cluster is returned as `primaryReasonCluster` so
//     outcomes can be attributed and a Bayesian weight applied per-cluster.
//   - `clusterContribs` map exposes raw contribution before cluster caps so the
//     walk-forward validator can learn cluster effectiveness over time.
//   - Optional `clusterWeights` argument scales each cluster's contribution
//     before the cap. Defaults to all-1.0; supplied by the adaptive scoring
//     job when it's been enabled by the operator.
//
// Pure function — no API calls, no side effects.
// ============================================================================

export const CLUSTERS = ['momentum', 'trend', 'volatility', 'volume', 'structure', 'macro'];
export const MAX_CLUSTER_POINTS = 2;

export function scoreTickerSignal(q, tech, {
  breadthRatio = 0.5,
  indexChangePct = 0,
  clusterWeights = null,   // { momentum: 1.0, trend: 0.8, ... } | null = all 1.0
  shortFlow = null,        // { ratio5d, ratio30dMean, ratio30dStd, n5, n30 } | null
                           // Surfaced by the FINRA daily Reg SHO job. Boehmer-
                           // Jones-Zhang (2008): top-decile short-flow predicts
                           // -1.16% over next 20 trading days. Read in the
                           // MACRO cluster below; safe to omit.
  insiderCluster = null,   // { buys30d, sells30d, distinctBuyers30d,
                           // distinctSellers30d, buyValueUsd30d } | null
                           // Surfaced by the SEC Form 4 daily job. Cohen-
                           // Malloy-Pomorski (2012): ≥2 independent insider
                           // buys in 30d = ~3.8% abnormal return over next
                           // 21 trading days. Read in the MACRO cluster.
} = {}) {
  const price = (q && q.price) || 0;
  const isRealTech = tech?._source === 'polygon';
  let dir = 'HOLD';
  const reasons = [];

  // Raw contributions per cluster — accumulated, then capped before summed.
  const raw = { momentum: 0, trend: 0, volatility: 0, volume: 0, structure: 0, macro: 0 };
  const addCluster = (cluster, points, reason) => {
    raw[cluster] = (raw[cluster] || 0) + points;
    if (reason && points > 0) reasons.push(reason);
  };

  // ── MOMENTUM cluster — RSI, weekly RSI, Stoch, MACD momentum ──
  if (tech.rsi != null) {
    if      (tech.rsi < 30) { dir = 'LONG';  addCluster('momentum', 2, `RSI oversold (${tech.rsi.toFixed(0)})`); }
    else if (tech.rsi > 70) { dir = 'SHORT'; addCluster('momentum', 2, `RSI overbought (${tech.rsi.toFixed(0)})`); }
    else if (tech.rsi < 40) { if (dir === 'HOLD') dir = 'LONG';  addCluster('momentum', 1, 'RSI low zone'); }
    else if (tech.rsi > 60) { if (dir === 'HOLD') dir = 'SHORT'; addCluster('momentum', 1, 'RSI high zone'); }
  }
  if (tech.macd != null && tech.macdSignal != null) {
    const macdBull = tech.macd > tech.macdSignal;
    const rsiNeutral = tech.rsi == null || (tech.rsi >= 40 && tech.rsi <= 60);
    if (macdBull) {
      if      (dir === 'LONG')  addCluster('momentum', 1, 'MACD confirms bullish');
      else if (rsiNeutral)      { dir = 'LONG'; addCluster('momentum', 1, 'MACD bullish'); }
    } else {
      if      (dir === 'SHORT') addCluster('momentum', 1, 'MACD confirms bearish');
      else if (rsiNeutral)      { dir = 'SHORT'; addCluster('momentum', 1, 'MACD bearish'); }
    }
  }
  if (tech.rsiWeekly != null) {
    if      (dir === 'LONG'  && tech.rsiWeekly < 60) addCluster('momentum', 1, `Weekly RSI ${tech.rsiWeekly.toFixed(0)}`);
    else if (dir === 'SHORT' && tech.rsiWeekly > 40) addCluster('momentum', 1, `Weekly RSI ${tech.rsiWeekly.toFixed(0)}`);
  }
  let stochConfirms = false;
  if (tech.stochK != null && tech.stochD != null) {
    const k = tech.stochK, d = tech.stochD;
    if (dir === 'LONG') {
      if      (k < 20 && d < 20) { addCluster('momentum', 2, `Stoch oversold K${k.toFixed(0)}/D${d.toFixed(0)}`); stochConfirms = true; }
      else if (k < 30 && k > d)  { addCluster('momentum', 1, `Stoch bullish cross K${k.toFixed(0)}`); stochConfirms = true; }
      else if (k > 80)            raw.momentum -= 1;
    } else if (dir === 'SHORT') {
      if      (k > 80 && d > 80) { addCluster('momentum', 2, `Stoch overbought K${k.toFixed(0)}/D${d.toFixed(0)}`); stochConfirms = true; }
      else if (k > 70 && k < d)  { addCluster('momentum', 1, `Stoch bearish cross K${k.toFixed(0)}`); stochConfirms = true; }
      else if (k < 20)            raw.momentum -= 1;
    }
  }

  // ── TREND cluster — SuperTrend, EMA50, weekly MACD ──
  if (tech.macdWeekly != null && tech.macdSignalWeekly != null) {
    const wkBull = tech.macdWeekly > tech.macdSignalWeekly;
    if      (dir === 'LONG'  && wkBull)  addCluster('trend', 1, 'Weekly MACD bullish');
    else if (dir === 'SHORT' && !wkBull) addCluster('trend', 1, 'Weekly MACD bearish');
  }
  let supertrendAligned = false;
  if (tech.supertrendUp != null) {
    if (dir === 'LONG') {
      if  (tech.supertrendUp) { addCluster('trend', 2, 'SuperTrend bullish'); supertrendAligned = true; }
      else                    raw.trend -= 1;
    } else if (dir === 'SHORT') {
      if  (!tech.supertrendUp) { addCluster('trend', 2, 'SuperTrend bearish'); supertrendAligned = true; }
      else                     raw.trend -= 1;
    }
  }
  let ema50Aligned = false;
  if (tech.ema50 != null && price > 0) {
    if      (dir === 'LONG'  && price > tech.ema50) { addCluster('trend', 1, 'Above EMA50'); ema50Aligned = true; }
    else if (dir === 'SHORT' && price < tech.ema50) { addCluster('trend', 1, 'Below EMA50'); ema50Aligned = true; }
  }
  // ADX is a trend-strength filter, not a directional cluster — small +/- here.
  if (tech.adx != null) {
    if      (tech.adx < 20) raw.trend -= 1;
    else if (tech.adx > 25) addCluster('trend', 1, `Trend ADX ${tech.adx.toFixed(0)}`);
  }

  // ── VOLATILITY cluster — Bollinger breaches, 52-wk proximity ──
  if (tech.bollingerLower != null && tech.bollingerUpper != null && price > 0) {
    if      (price < tech.bollingerLower) { if (dir !== 'SHORT') dir = 'LONG';  addCluster('volatility', 2, 'Below Bollinger Lower'); }
    else if (price > tech.bollingerUpper) { dir = 'SHORT'; addCluster('volatility', 2, 'Above Bollinger Upper'); }
  }
  let near52High = false, near52Low = false;
  if (q.week52High != null && q.week52Low != null && q.week52High > 0 && price > 0) {
    near52High = (q.week52High - price) / q.week52High * 100 <= 5;
    near52Low  = (price - q.week52Low)  / price           * 100 <= 5;
    if (dir === 'LONG') {
      if      (near52High) addCluster('volatility', 2, 'Breakout: near 52-wk high');
      else if (near52Low)  addCluster('volatility', 1, 'Mean-reversion: near 52-wk low');
    } else if (dir === 'SHORT') {
      if      (near52Low)  addCluster('volatility', 2, 'Breakdown: near 52-wk low');
      else if (near52High) addCluster('volatility', 1, 'Extended: near 52-wk high');
    }
  }

  // ── VOLUME cluster — volume spike, OBV ──
  if ((q.avgVolume || 0) > 0 && (q.volume || 0) > q.avgVolume * 1.5) {
    addCluster('volume', 1, 'High volume');
  }
  if (tech.obvRising != null) {
    if      (dir === 'LONG'  && tech.obvRising)  addCluster('volume', 1, 'OBV rising (accumulation)');
    else if (dir === 'SHORT' && !tech.obvRising) addCluster('volume', 1, 'OBV falling (distribution)');
  }

  // ── STRUCTURE cluster — VWAP, pivots, intraday range break ──
  if (q.vwap != null && price > 0) {
    const vwapPct = +((price - q.vwap) / q.vwap * 100).toFixed(1);
    if      (dir === 'LONG'  && price > q.vwap) addCluster('structure', 1, `Above VWAP +${vwapPct}%`);
    else if (dir === 'SHORT' && price < q.vwap) addCluster('structure', 1, `Below VWAP ${vwapPct}%`);
  }
  if ((q.high || 0) > 0 && (q.low || 0) > 0 && price > 0 && q.high !== q.low) {
    const pivot = (q.high + q.low + price) / 3;
    const r1 = 2 * pivot - q.low, s1 = 2 * pivot - q.high;
    if (dir === 'LONG'  && Math.abs(price - s1) / price * 100 <= 1.5) addCluster('structure', 1, `Near pivot S1 ≈${s1.toFixed(2)}`);
    if (dir === 'SHORT' && Math.abs(price - r1) / price * 100 <= 1.5) addCluster('structure', 1, `Near pivot R1 ≈${r1.toFixed(2)}`);
  }
  // Strong intraday moves only CONFIRM a direction set by a durable indicator
  // (RSI / BB / MACD / EMA50 cross). They never set direction themselves —
  // that's late-entry momentum chasing. Against-direction moves are penalised
  // to discourage knife-catching and shorting-into-strength.
  if (Math.abs(q.changePct || 0) > 1.5) {
    const up = q.changePct > 0;
    if      (up  && dir === 'LONG')  addCluster('structure', 1, `Confirmed by +${q.changePct.toFixed(1)}% today`);
    else if (!up && dir === 'SHORT') addCluster('structure', 1, `Confirmed by ${q.changePct.toFixed(1)}% today`);
    else if (up  && dir === 'SHORT') raw.structure -= 1;
    else if (!up && dir === 'LONG')  raw.structure -= 1;
  }

  // ── MACRO cluster — breadth, relative strength vs index ──
  if (breadthRatio > 0.55) {
    if      (dir === 'LONG')  addCluster('macro', 1, `${Math.round(breadthRatio * 100)}% market advancing`);
    else if (dir === 'SHORT') raw.macro -= 1;
  } else if (breadthRatio < 0.45) {
    if      (dir === 'SHORT') addCluster('macro', 1, `${Math.round((1 - breadthRatio) * 100)}% market declining`);
    else if (dir === 'LONG')  raw.macro -= 1;
  }
  const rsVsIndex = (q.changePct || 0) - indexChangePct;
  const strongRS = (dir === 'LONG' && rsVsIndex > 2.0) || (dir === 'SHORT' && rsVsIndex < -2.0);
  if (dir === 'LONG') {
    if      (rsVsIndex > 2.0)  addCluster('macro', 2, `RS +${rsVsIndex.toFixed(1)}% vs index`);
    else if (rsVsIndex > 0.8)  addCluster('macro', 1, `RS +${rsVsIndex.toFixed(1)}% vs index`);
    else if (rsVsIndex < -2.5) raw.macro -= 1;
  } else if (dir === 'SHORT') {
    if      (rsVsIndex < -2.0) addCluster('macro', 2, `RS ${rsVsIndex.toFixed(1)}% vs index`);
    else if (rsVsIndex < -0.8) addCluster('macro', 1, `RS ${rsVsIndex.toFixed(1)}% vs index`);
    else if (rsVsIndex > 2.5)  raw.macro -= 1;
  }

  // ── MACRO cluster — short-flow z-score (FINRA Reg SHO) ──
  //
  // Compute a z-score for the trailing 5-day short-volume ratio against the
  // ticker's own trailing-30-day distribution. A z above +1 means short flow
  // is meaningfully elevated for this name (informed sellers leaning in); a
  // z below -1 means it's depressed (short sellers stepping back).
  //
  // Per Boehmer-Jones-Zhang (2008), high short flow predicts negative returns
  // over the next ~20 trading days. The contribution is asymmetric: elevated
  // flow only adds points when we're already on the SHORT side (i.e., the
  // technicals agree); it doesn't flip direction. Conversely, depressed flow
  // gives a small bump to LONG candidates. Requires n30 ≥ 10 to compute
  // (smaller samples have unreliable std).
  if (shortFlow && shortFlow.n30 >= 10 && shortFlow.ratio30dStd > 0.001) {
    const z = (shortFlow.ratio5d - shortFlow.ratio30dMean) / shortFlow.ratio30dStd;
    if (z >= 1.5) {
      if      (dir === 'SHORT') addCluster('macro', 2, `Short flow +${z.toFixed(1)}σ`);
      else if (dir === 'LONG')  raw.macro -= 1;  // informed sellers disagreeing with our LONG
    } else if (z >= 0.8) {
      if      (dir === 'SHORT') addCluster('macro', 1, `Short flow +${z.toFixed(1)}σ`);
    } else if (z <= -1.2) {
      if      (dir === 'LONG')  addCluster('macro', 1, `Short flow ${z.toFixed(1)}σ`);
    }
  }

  // ── MACRO cluster — insider cluster buys (SEC Form 4) ──
  //
  // Cohen-Malloy-Pomorski (2012) + Alldredge (2019): when ≥2 unrelated
  // insiders independently open-market-purchase shares within 30 days, the
  // stock earns ~3.8% abnormal return over the next 21 trading days. The
  // mechanism is information-content: insiders rarely buy their own stock
  // on the open market unless they're seeing something positive. Net selling
  // by clusters has weaker but still meaningful predictive power on the
  // SHORT side.
  //
  // Asymmetric contribution mirrors the short-flow block: cluster buys
  // confirm LONG and discourage SHORT; cluster sells confirm SHORT and
  // discourage LONG. Won't flip direction; only confirms what technicals
  // already point at.
  if (insiderCluster) {
    const buyers  = insiderCluster.distinctBuyers30d  || 0;
    const sellers = insiderCluster.distinctSellers30d || 0;
    if (buyers >= 3) {
      if      (dir === 'LONG')  addCluster('macro', 2, `${buyers} insiders bought`);
      else if (dir === 'SHORT') raw.macro -= 1;
    } else if (buyers === 2) {
      if      (dir === 'LONG')  addCluster('macro', 1, `${buyers} insiders bought`);
    }
    if (sellers >= 3 && buyers === 0) {
      if      (dir === 'SHORT') addCluster('macro', 1, `${sellers} insiders sold`);
      else if (dir === 'LONG')  raw.macro -= 1;
    }
  }

  // ── Cluster caps + adaptive weights ──
  // Each cluster's contribution is bounded by MAX_CLUSTER_POINTS so a single
  // setup family can't dominate the score; an adaptive weight (from walk-
  // forward results, defaults to 1.0) then scales the capped contribution.
  // Negative raw values (counter-trend penalties) are passed through without
  // the cap so they still discourage low-quality setups.
  const clusterContribs = {};
  let score = 0;
  for (const c of CLUSTERS) {
    const rawC = raw[c] || 0;
    const capped = rawC > 0 ? Math.min(MAX_CLUSTER_POINTS, rawC) : rawC;
    const weight = clusterWeights?.[c] != null ? clusterWeights[c] : 1.0;
    const weighted = +(capped * weight).toFixed(2);
    clusterContribs[c] = weighted;
    score += weighted;
  }
  score = +score.toFixed(2);

  const primaryReasonCluster = Object.entries(clusterContribs)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  let confidence = 30 + score * 6 + (isRealTech ? 15 : 0);
  if (tech.rsi != null && (tech.rsi < 25 || tech.rsi > 75)) confidence += 5;
  if ((q.volume || 0) > (q.avgVolume || 0) * 2) confidence += 5;
  if (tech.adx != null) {
    if      (tech.adx > 40) confidence += 8;
    else if (tech.adx > 25) confidence += 4;
    else if (tech.adx < 20) confidence -= 8;
  }
  if (q.vwap != null && price > 0 && ((dir === 'LONG' && price > q.vwap) || (dir === 'SHORT' && price < q.vwap))) confidence += 5;
  if (tech.rsiWeekly != null && ((dir === 'LONG' && tech.rsiWeekly < 60) || (dir === 'SHORT' && tech.rsiWeekly > 40))) confidence += 5;
  if (tech.obvRising != null && ((dir === 'LONG' && tech.obvRising) || (dir === 'SHORT' && !tech.obvRising))) confidence += 4;
  if ((breadthRatio > 0.55 && dir === 'LONG') || (breadthRatio < 0.45 && dir === 'SHORT')) confidence += 4;
  if (strongRS) confidence += 6;
  if (near52High && dir === 'LONG')  confidence += 8;
  if (near52Low  && dir === 'SHORT') confidence += 8;
  if (stochConfirms)     confidence += 5;
  if (supertrendAligned) confidence += 8;
  if (ema50Aligned)      confidence += 4;
  confidence = Math.min(95, Math.max(20, confidence));

  return {
    direction: dir,
    score,
    confidence,
    reasons,
    clusterContribs,
    primaryReasonCluster,
  };
}
